import { Box, Button, Card, TextField, Typography, FormControlLabel, Radio, RadioGroup, FormControl, FormLabel, CircularProgress } from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { api } from "../../services/api.service";
import { setUserInfo } from "../../store/userInfoSlice";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../router/constants";

export function UserProfileForm() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.userInfo);

  const [formData, setFormData] = useState({
    address: "",
    city: "",
    state: "",
    zip_code: "",
    birthDay: "",
    registrationNumber: "",
    phone: "",
    socialProgram: null as boolean | null,
    programName: "",
  });

  const [submitedError, setSubmitedError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchingCep, setIsSearchingCep] = useState(false);

  const applyMask = (value: string, maskType: 'cpf' | 'phone' | 'cep') => {
    const numbers = value.replace(/\D/g, '');
    
    if (maskType === 'cpf') {
      return numbers
        .slice(0, 11)
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    
    if (maskType === 'phone') {
      return numbers
        .slice(0, 11)
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2');
    }
    
    if (maskType === 'cep') {
      return numbers
        .slice(0, 8)
        .replace(/(\d{5})(\d)/, '$1-$2');
    }
    
    return value;
  };

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    
    let finalValue = value;
    
    // Converter radio de socialProgram para boolean
    if (name === 'socialProgram') {
      finalValue = value === 'sim' ? true : value === 'nao' ? false : null;
    }
    
    // Aplicar máscaras
    if (name === 'registrationNumber' && typeof value === 'string') {
      finalValue = applyMask(value, 'cpf');
    } else if (name === 'phone' && typeof value === 'string') {
      finalValue = applyMask(value, 'phone');
    } else if (name === 'zip_code' && typeof value === 'string') {
      finalValue = applyMask(value, 'cep');
    }
    
    setFormData((prev) => ({ 
      ...prev, 
      [name]: finalValue 
    }));
  };

  const removeMask = (value: string) => {
    return value.replace(/\D/g, '');
  };

  const validateCPF = (cpf: string) => {
    const numbers = cpf.replace(/\D/g, '');
    
    if (numbers.length !== 11) return false;
    
    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(numbers)) return false;
    
    // Validação dos dígitos verificadores
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(numbers.charAt(i)) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(numbers.charAt(9))) return false;
    
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(numbers.charAt(i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(numbers.charAt(10))) return false;
    
    return true;
  };

  const validatePhone = (phone: string) => {
    const numbers = phone.replace(/\D/g, '');
    return numbers.length === 10 || numbers.length === 11;
  };

  const validateCEP = (cep: string) => {
    const numbers = cep.replace(/\D/g, '');
    return numbers.length === 8;
  };

  const handleSearchCep = async () => {
    const cepNumbers = removeMask(formData.zip_code);

    if (!cepNumbers || cepNumbers.length !== 8) {
      toast.error("Informe um CEP válido com 8 dígitos para buscar o endereço");
      return;
    }

    try {
      setIsSearchingCep(true);
      const response = await fetch(`https://viacep.com.br/ws/${cepNumbers}/json/`);
      if (!response.ok) {
        throw new Error("Erro ao buscar CEP");
      }

      const data = await response.json();

      if ((data as any).erro) {
        toast.error("CEP não encontrado");
        return;
      }

      const street: string = data.logradouro || "";
      const neighborhood: string = data.bairro || "";
      let composedAddress = street;

      if (street && neighborhood) {
        composedAddress = `${street} - ${neighborhood}`;
      } else if (!street && neighborhood) {
        composedAddress = neighborhood;
      }

      setFormData((prev) => ({
        ...prev,
        address: composedAddress || prev.address,
        city: data.localidade || prev.city,
        state: data.uf || prev.state,
      }));
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      toast.error("Não foi possível buscar o endereço pelo CEP");
    } finally {
      setIsSearchingCep(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitedError(true);
    setIsLoading(true);
    
    // Validação de campos obrigatórios
    if (!formData.address || !formData.city || !formData.state || !formData.zip_code || !formData.birthDay || !formData.phone || !formData.registrationNumber) {
      toast.error("Por favor, preencha todos os campos obrigatórios");
      setIsLoading(false);
      return;
    }
    
    // Validação do CPF
    if (!validateCPF(formData.registrationNumber)) {
      toast.error("CPF inválido. Verifique o número digitado");
      setIsLoading(false);
      return;
    }
    
    // Validação do telefone
    if (!validatePhone(formData.phone)) {
      toast.error("Telefone inválido. Deve conter 10 ou 11 dígitos");
      setIsLoading(false);
      return;
    }
    
    // Validação do CEP
    if (!validateCEP(formData.zip_code)) {
      toast.error("CEP inválido. Deve conter 8 dígitos");
      setIsLoading(false);
      return;
    }

    try {
      // Remover máscaras antes de enviar para a API
      const dataToSend = {
        ...formData,
        registrationNumber: formData.registrationNumber ? removeMask(formData.registrationNumber) : '',
        phone: removeMask(formData.phone),
        zip_code: removeMask(formData.zip_code)
      };
      
      const { data } = await api.post(`/user/profile`, dataToSend);
      
      console.log('Profile response:', data);
      
      // Atualizar a store com o novo perfil
      // Garantir que isValidated seja true se não vier da API
      const updatedProfile = {
        ...data,
        isValidated: data.isValidated !== undefined ? data.isValidated : true
      };
      
      dispatch(setUserInfo({
        ...user,
        profile: updatedProfile
      }));

      toast.success("Perfil atualizado com sucesso!");
      
      // Usar setTimeout para garantir que o Redux seja atualizado antes da navegação
      setTimeout(() => {
        navigate(ROUTES.HOME);
      }, 100);
    } catch (error: any) {
      toast.error("Erro ao salvar perfil. Tente novamente.");
      console.error("Erro ao salvar perfil:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      padding={4}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "calc(100vh - 64px)",
      }}
    >
      <Card
        sx={{
          padding: 4,
          maxWidth: 800,
          width: "100%",
        }}
      >
        <Typography variant="h5" fontWeight={600} color="#505152ff" mb={1}>
          Complete seu Perfil
        </Typography>
        <Typography variant="body2" color="#8d8f92ff" mb={3}>
          Para continuar, precisamos de algumas informações adicionais
        </Typography>

        <Box display="flex" flexDirection="column" gap={3} width="100%">
          {/* CEP primeiro campo com busca automática */}
          <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
            <TextField
              label="CEP"
              fullWidth
              name="zip_code"
              required
              value={formData.zip_code}
              onChange={handleChange}
              error={submitedError && (!formData.zip_code || !validateCEP(formData.zip_code))}
              helperText={
                submitedError && !formData.zip_code
                  ? "O CEP é obrigatório"
                  : submitedError && !validateCEP(formData.zip_code)
                  ? "CEP deve ter 8 dígitos"
                  : ""
              }
            />

            <Button
              variant="outlined"
              onClick={handleSearchCep}
              disabled={isSearchingCep}
              sx={{
                minWidth: { xs: "100%", sm: 180 },
                mt: { xs: 0, sm: "auto" },
                height: 56,
              }}
            >
              {isSearchingCep ? "Buscando..." : "Buscar CEP"}
            </Button>
          </Box>

          <TextField
            label="Endereço"
            fullWidth
            name="address"
            required
            value={formData.address}
            onChange={handleChange}
            error={submitedError && !formData.address}
            helperText={submitedError && !formData.address ? "O endereço é obrigatório" : ""}
          />

          <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
            <TextField
              label="Cidade"
              fullWidth
              name="city"
              required
              value={formData.city}
              onChange={handleChange}
              error={submitedError && !formData.city}
              helperText={submitedError && !formData.city ? "A cidade é obrigatória" : ""}
            />

            <TextField
              label="Estado"
              fullWidth
              name="state"
              required
              value={formData.state}
              onChange={handleChange}
              error={submitedError && !formData.state}
              helperText={submitedError && !formData.state ? "O estado é obrigatório" : ""}
            />
          </Box>

          <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
            <TextField
              label="Data de Nascimento"
              fullWidth
              name="birthDay"
              type="date"
              required
              value={formData.birthDay}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              error={submitedError && !formData.birthDay}
              helperText={submitedError && !formData.birthDay ? "A data de nascimento é obrigatória" : ""}
            />
          </Box>

          <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
            <TextField
              label="CPF"
              fullWidth
              name="registrationNumber"
              required
              value={formData.registrationNumber}
              onChange={handleChange}
              error={submitedError && (!formData.registrationNumber || !validateCPF(formData.registrationNumber))}
              helperText={submitedError && !formData.registrationNumber ? "O CPF é obrigatório" : submitedError && !validateCPF(formData.registrationNumber) ? "CPF inválido" : ""}
            />

            <TextField
              label="Telefone"
              fullWidth
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              error={submitedError && (!formData.phone || !validatePhone(formData.phone))}
              helperText={submitedError && !formData.phone ? "O telefone é obrigatório" : submitedError && !validatePhone(formData.phone) ? "Telefone deve ter 10 ou 11 dígitos" : ""}
            />
          </Box>

          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 1, color: "rgba(0, 0, 0, 0.6)", fontSize: "0.875rem" }}>
              É inscrito no CadÚnico ou algum programa social do estado?
            </FormLabel>
            <RadioGroup
              name="socialProgram"
              value={formData.socialProgram === true ? "sim" : formData.socialProgram === false ? "nao" : ""}
              onChange={handleChange}
              row
            >
              <FormControlLabel value="sim" control={<Radio />} label="Sim" />
              <FormControlLabel value="nao" control={<Radio />} label="Não" />
            </RadioGroup>
          </FormControl>

          {formData.socialProgram === true && (
            <TextField
              label="Nome do Programa"
              fullWidth
              name="programName"
              value={formData.programName}
              onChange={handleChange}
            />
          )}

          <Button
            variant="contained"
            fullWidth
            sx={{ height: "50px", mt: 2 }}
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} color="inherit" />
                {formData.socialProgram === true ? "Validando no CadÚnico..." : "Salvando..."}
              </Box>
            ) : (
              "Salvar Perfil"
            )}
          </Button>
        </Box>
      </Card>
    </Box>
  );
}
